import { ReactNode, forwardRef } from 'react';

import { PropsWithCN, cn } from '@tg-web/utils';

export type IslandTextareaCellProps = Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> &
  PropsWithCN & {
    leftIcon?: ReactNode;
  };

/**
 * A tall row whose content is a free-text area. The icon is pinned to the top
 * rather than centred, so it stays beside the first line as the text grows.
 *
 * 160px is the mock's resting height; the textarea fills it and scrolls beyond
 * it, which keeps the island's own height stable while someone types.
 */
export const IslandTextareaCell = forwardRef<
  HTMLTextAreaElement,
  IslandTextareaCellProps
>(({ className, leftIcon, ...props }, ref) => {
  return (
    <div
      className={cn('flex min-h-[160px] w-full items-start px-4 pt-4', className)}
    >
      {leftIcon && (
        <div className="text-tg-section-header-text mr-[18px] flex h-[26px] w-[26px] shrink-0 items-center justify-center">
          {leftIcon}
        </div>
      )}
      {/*
       * The same 3px gutter `IslandCell` and `IslandInputCell` give their
       * content, and for the same reason: a 20px first line inside a 26px icon
       * slot needs (26 - 20) / 2 to sit level with the glyph. On the top edge
       * only — as the text wraps, the icon stays on the first line rather than
       * drifting to the centre of the block.
       */}
      <div className="min-w-0 flex-1 pt-[3px]">
        <textarea
          className="typo-text text-tg-text placeholder:text-tg-hint min-h-[120px] w-full resize-none border-0 bg-transparent p-0 outline-none focus-visible:outline-none"
          ref={ref}
          {...props}
        />
      </div>
    </div>
  );
});

IslandTextareaCell.displayName = 'IslandTextareaCell';
